import {
  Component,
  forwardRef,
  OnChanges,
  OnDestroy,
  Input,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  TemplateRef,
  SimpleChanges,
  ViewEncapsulation,
  Output,
  EventEmitter
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { ScriptService } from './tinymce.script.service';
import { NaTinymceOptions } from './na-tinymce.options';

declare const window: any;
declare const tinymce: any;

@Component({
  selector: 'na-tinymce',
  templateUrl: './na-tinymce.component.html',
  styles: [ `na-tinymce .tinymce-selector { display: none; }` ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NaTinymceComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NaTinymceComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  private _instance: any;
  private value: string;
  load = true;
  id = `_tinymce-${Math.random().toString(36).substring(2)}`;

  private onChange: (value: string) => void;
  private onTouched: () => void;

  @Input() config: any;
  @Input() placeholder = '';
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    this.setDisabled();
  }
  private _disabled = false;

  _loading: string;
  _loadingTpl: TemplateRef<any>;
  @Input()
  set loading(value: string | TemplateRef<any>) {
    if (value instanceof TemplateRef) {
      this._loading = null;
      this._loadingTpl = value;
    } else {
      this._loading = value;
    }
  }
  /** å»¶è¿åå§å */
  @Input() delay = 0;
  @Output() ready = new EventEmitter<any>();

  get instance() {
    return this._instance;
  }

  constructor(
    private defConfig: NaTinymceOptions,
    private ss: ScriptService,
    private cd: ChangeDetectorRef,
  ) {}

  private initDelay() {
    if (this.delay > 0) {
      setTimeout(() => this.init(), this.delay);
    } else {
      this.init();
    }
  }

  private init() {
    if (!window.tinymce) {
      throw new Error('tinymce jsæä»¶å è½½å¤±è´¥');
    }

    const { defConfig, config, id } = this;
    if (this.instance) {
      return;
    }

    if (defConfig.baseURL) {
      let url = '' + defConfig.baseURL;
      if (url.endsWith('/')) {
        url = url.substr(0, url.length - 1);
      }
      tinymce.baseURL = url;
    }

    const userOptions = Object.assign({}, defConfig.config, config);

    const options = Object.assign(
      {
        selector: `#` + id,
      },
      defConfig.config,
      config,
      {
        setup: (editor: any) => {
          this._instance = editor;
          editor.on('change keyup', () => {
            this.value = editor.getContent();
            this.onChange(this.value);
            this.cd.detectChanges();
          });
          if (typeof userOptions.setup === 'function') {
            userOptions.setup(editor);
          }
        },
        init_instance_callback: (editor: any) => {
          if (editor && this.value) {
            editor.setContent(this.value);
          }
          this.setDisabled();
          if (typeof userOptions.init_instance_callback === 'function') {
            userOptions.init_instance_callback(editor);
          }
          this.ready.emit(this._instance);
          this.load = false;
          this._loading = null;
        },
      },
    );
    if (userOptions.auto_focus) {
      options.auto_focus = id;
    }

    tinymce.init(options);

    this.cd.detectChanges();
  }

  private destroy() {
    if (!this._instance) {
      return;
    }
    // è§£é¤ç»å®çææäºä»¶
    this._instance.off();
    this._instance.remove('#' + this.id);
    this._instance = null;
  }

  private setDisabled() {
    if (!this._instance) {
      return;
    }
    this._instance.setMode(this._disabled ? 'readonly' : 'design');
  }

  ngAfterViewInit(): void {
    // å·²ç»å­å¨å¯¹è±¡æ é¡»è¿å¥æå è½½æ¨¡å¼
    if (window.tinymce) {
      this.initDelay();
      return;
    }

    const { defConfig } = this;
    const baseURL = defConfig && defConfig.baseURL;
    const fileName = defConfig && defConfig.fileName;
    this.ss
      .load((baseURL || '/assets/tinymce/') + (fileName || 'tinymce.min.js'))
      .getChangeEmitter()
      .subscribe(() => this.initDelay());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._instance && changes.config) {
      this.destroy();
      this.initDelay();
    }
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  writeValue(value: string): void {
    // value should be NOT NULL
    this.value = value || '';
    if (this._instance) {
      this._instance.setContent(this.value);
    }
  }

  registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.setDisabled();
  }
}
